import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import NewLayout from "@components/new-ui/Layout";
import { isJSXElement } from "@utils/common";
import Image from "next/image";
import React, { ReactNode, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { CasestudyCard, casestudyCards } from ".";
import { container_classname } from "@components/new-ui/constants";

interface CaseStudyHerosectionProps {
    title: string;
    description?: string;
    imageUrl: string;
    redirectUrl: string;
}
interface CompanyProfileItem {
    label: string;
    value: string[];
}

interface CaseStudySubDetails {
    title: string;
    description?: string[];
}

interface CaseStudySubInformation {
    content: CaseStudySubDetails[];
    className?: string;
}

interface ProductLink {
    link: string;
    label: string;
}

interface ProductItems {
    title: string;
    productLink: ProductLink[];
}

interface OrganizationProfile {
    companyProfileData: CompanyProfileItem[];
    product: ProductItems;
}

interface CaseStudyInformation {
    mainTitle?: string | JSX.Element;
    subTitle?: string | JSX.Element;
    descriptions?: string[];
    className?: string;
}

interface FeatureListItemProps {
    listItem: string | JSX.Element;
    nestedListItem?: string[];
}
interface FeatureListCardProps {
    List: FeatureListItemProps[];
    title?: string;
    className?: string;
    Icon?: ReactNode;
    description?: string;
}

interface ClientFeedbackProps {
    descriptions?: string | JSX.Element;
    authorName?: string;
    designationWithServiceName?: string;
    className?: string;
}

export const navLinks = [
    { id: "About", label: "About" },
    { id: "Challenges", label: "Challenges" },
    { id: "Solutions", label: "Solutions" },
    { id: "Impact", label: "Impact" },
];

const Alrannan: React.FC = () => {
    return (
        <NewLayout
            title="Alrannan Administrative Services | WhatsApp Business customer story"
            description="Find out how Alrannan leveraged Gallabox to transform their client communications, boost team productivity by 10%, and set the stage for 50% growth through Whatsapp automation."
            canonical="https://gallabox.com/case-study/alrannan-administrative-services"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-og.png" }}
        >
            <CaseStudyHerosection
                title="How Alrannan Administrative Services automated sales with Gallabox"
                description="Learn how Alrannan leveraged Gallabox to transform their client communications, boost team productivity by 10%, and set the stage for 50% growth through WhatsApp automation."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "10%", label: "Increase in team productivity" },
                    { percentage: "45%", label: "Projected quarterly business growth" },
                ]}
            />

            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Alrannan Administrative Services"] },
                                { label: "Industry", value: ["Services"] },
                                { label: "Employees", value: ["50-200"] },
                                { label: "Team", value: ["Marketing"] },
                            ]}
                            product={{
                                title: "Product Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared Team Inbox" },
                                    { link: "https://gallabox.com/whatsapp-ai-chatbot", label: "Gallabox Gen AI" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col">
                        <section id="About">
                            <CaseStudyInformation mainTitle={" consultancy that makes things happen for businesses looking to establish a moat in the UAE"} />
                            <p>
                                <a href=" https://www.alrannan.ae" className="text-blue-500">
                                    Alrannan Administrative Services&nbsp;
                                </a>
                                specializes in helping international businesses establish and thrive in the UAE market. Beyond just handling business setup, they provide
                                comprehensive support in acquiring statutory licenses, securing necessary approvals, and facilitating market entry through their extensive network.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"The challenge: Traditional channels were costly and ineffective"}
                                descriptions={[
                                    "For an agency that requires a lot of high-touch interactions with large accounts, Alrannan faced several communication hurdles in their day-to-day operations. D Gangadhhara Swamy, Alrannan’s Founder & CEO, had identified the pain points before they snowballed into something difficult to fix:",
                                ]}
                                className="gap-4"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "High costs associated with traditional phone calls" },
                                    { listItem: "Unresponsive email communications" },
                                    { listItem: "Limited functionality of basic WhatsApp Business" },
                                    { listItem: "Need for enterprise-grade automation to handle high volumes of incoming leads" },
                                    { listItem: "Resource constraints in managing customer communications." },
                                ]}
                                className="mt-5 bg-gray-100 mb-10"
                            />
                            <CaseStudyInformation
                                subTitle={"From evaluation to re-evaluation to sticking with Gallabox"}
                                descriptions={[
                                    "The journey to finding the perfect WhatsApp Business API solution wasn't straightforward. The team explored various vendors including Wati and Interakt before discovering Gallabox. But three key factors that set Gallabox apart:",
                                ]}
                            />

                            <FeatureList
                                List={[{ listItem: "Ease of using the product" }, { listItem: "Robust integrations" }, { listItem: "Outstanding customer support." }]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                descriptions={[
                                    "But after initially implementing Gallabox, they briefly switched to Respond.io. However, their experience led them back to Gallabox, impressed by our:",
                                ]}
                                className="gap-4"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Comprehensive product features and integrations" },
                                    { listItem: "User-friendly interface" },
                                    { listItem: "Exceptional customer support response times" },
                                    { listItem: "The founder's vision and product understanding" },
                                    { listItem: "Gallabox’s convincing positioning of WhatsApp as the most cost-effective communication channel in Dubai" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-8">
                                &quot;The kind of response your support team gives, for example… whatever questions we ask, they just respond and solve the problem, he noted,
                                emphasizing the exceptional support that influenced their decision.&quot;
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"Implementation strategy"}
                                descriptions={["The consultancy adopted a phased approach to transforming their business communication on WhatsApp using Gallabox:"]}
                                className="gap-4 mt-12"
                            />
                            <FeatureList
                                title="Phase 1"
                                List={[{ listItem: "Ease of using the product" }, { listItem: "Robust integrations" }, { listItem: "Outstanding customer support." }]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-6">
                                &quot;We get plenty of incoming leads. We don&apos;t have time to handle that, and we don&apos;t have manpower to do that. So in the first phase, we
                                are trying to automate that process, he explained.&quot;
                            </p>
                            <FeatureList
                                title="Future phases"
                                List={[
                                    { listItem: "Expansion into marketing automation" },
                                    { listItem: "Integration with customer support" },
                                    { listItem: "Implementation of broadcast features." },
                                    { listItem: "Enhanced lead management systems." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"Early impact and results paint a winning GTM strategy with Gallabox"}
                                descriptions={[
                                    "Despite being just a few days into their partnership with Gallabox, the consultancy had already witnessed significant improvements:",
                                ]}
                                className="mt-12 gap-5"
                            />
                            <FeatureList
                                title="Future phases"
                                List={[
                                    { listItem: "10% increase in team productivity" },
                                    { listItem: "Enhanced team confidence in customer interactions" },
                                    { listItem: "More professional communication approach" },
                                    { listItem: "Improved lead response capabilities" },
                                    { listItem: "Cost savings through automation versus hiring additional resources." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                descriptions={["Swamy has seen some early green shoots and has some insights into how Gallabox is going to help them grow their revenue."]}
                                className="mt-3"
                            />
                            <p className="mt-3">
                                &quot;My team is also excited to talk to customers... they feel more confident. So I think the numbers will increase over a period of time.&quot;
                            </p>
                            <CaseStudyInformation
                                subTitle={"The future of Alrannan’s Gallabox adoption is exciting"}
                                descriptions={["The consultancy has set ambitious goals for their Gallabox implementation:"]}
                                className="mt-12 gap-5"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Projected 40-50% business growth in the next quarter through Gallabox" },
                                    { listItem: "Plans to expand usage across marketing and customer support teams" },
                                    { listItem: "Focus on further automating lead management processes" },
                                    { listItem: "Enhanced customer engagement through automated sequences." },
                                ]}
                                className="mt-3 bg-gray-100"
                            />
                            <CaseStudyInformation
                                descriptions={[
                                    "By focusing on automating their communication processes through Gallabox, Alrannan is not just solving current challenges but building a scalable foundation for future growth. Their success story exemplifies how choosing the right communication partner for WhatsApp can transform business operations and drive sustainable growth in competitive markets like the UAE.",
                                ]}
                                className="mt-3 mb-4"
                            />
                            <ClientFeedback
                                descriptions="The kind of response your support team gives, for example… whatever questions we ask, they just respond and solve the problem."
                                authorName="D Gangadhhara Swamy"
                                designationWithServiceName="Founder & CEO, Alrannan Administrative Services"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto text-center md:text-left">
                <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 text-center md:text-left">Check out other customer stories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-20">
                    {casestudyCards.slice(3, 6)?.map((data, index) => (
                        <CasestudyCard key={index} {...data} />
                    ))}
                </div>
            </section>
        </NewLayout>
    );
};

export default Alrannan;

export const CaseStudyHerosection: React.FC<CaseStudyHerosectionProps> = ({ title, description, imageUrl, redirectUrl }) => {
    return (
        <section className="md:py-20 py-10 ">
            <div className={`${container_classname} mx-auto md:flex-row flex justify-between items-center mt-5 flex-col`}>
                <div className="md:w-1/2 w-full flex flex-col gap-y-8">
                    <a className="text-blue-500 font-medium text-lg md:flex items-center gap-x-2 hidden hover:no-underline" href={redirectUrl}>
                        <span className="">
                            <BiArrowBack className="hover:text-white  text-blue-500 hover:bg-blue-500 rounded-full p-1" size={30} />
                        </span>
                        Back to customer stories
                    </a>
                    <h1 className="md:text-7xl text-4xl font-bold text-left md:mb-2" style={{ fontFamily: "Degular Variable" }}>
                        {title}
                    </h1>
                    <p className="text-lg font-extralight tracking-wider text-left px-2 md:text-xl">{description}</p>
                </div>
                <style jsx>{`
                    .lineheight {
                        line-height: 40px;
                    }

                    @media (min-width: 768px) {
                        .lineheight {
                            line-height: 60px;
                        }
                    }
                `}</style>

                <Image src={imageUrl} height={428} width={428} alt="dubai-case-study" className="px-2 md:px-0 mt-10 md:mt-0  rounded-xl" />
            </div>
        </section>
    );
};

export const OrganizationProfile: React.FC<OrganizationProfile> = ({ companyProfileData, product }) => {
    return (
        <section>
            <div className="flex flex-col gap-y-8 bg-blue-50 mx-3 p-2 mb-10 md:mb-0 border-2 border-solid border-blue-200 rounded-lg md:bg-white md:p-0 md:border-none ">
                {companyProfileData.map((data) => {
                    return (
                        <div className="flex flex-col gap-3" key={data.label}>
                            <p className="text-gray-900 text-xl font-semibold">{data.label}</p>
                            {data.value.map((data) => (
                                <p className="text-gray-700" key={`${data}`}>
                                    {data}
                                </p>
                            ))}
                        </div>
                    );
                })}
                <div className="flex flex-col gap-2">
                    <p className="text-gray-900 text-xl font-semibold">{product.title}</p>
                    {product.productLink.map((data) => {
                        return (
                            <a key={data.label} className="text-blue-500 block mb-1" href={data.link}>
                                {data.label}
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export const CaseStudyInformation: React.FC<CaseStudyInformation> = ({ mainTitle, subTitle, descriptions, className }) => {
    return (
        <section className={`flex flex-col ${className ? className : "gap-5"} w-full`}>
            {mainTitle && isJSXElement(mainTitle) ? mainTitle : <h3 className="font-semibold text-4xl">{mainTitle}</h3>}
            {subTitle && isJSXElement(subTitle) ? subTitle : <h5 className="text-2xl font-medium">{subTitle}</h5>}
            <div className="flex flex-col gap-6 text-gray-900 text-base">
                {descriptions?.map((data, ind) => (
                    <p key={ind}>{data}</p>
                ))}
            </div>
        </section>
    );
};

export const CaseStudySubInformation: React.FC<CaseStudySubInformation> = ({ content, className }) => {
    return (
        <section className={`flex flex-col ${className ? className : "gap-5"} w-full`}>
            <div className="flex flex-col gap-3 text-gray-900 ">
                {content?.map((data) => (
                    <>
                        <p key={data.title} className="text-xl">
                            {data.title}
                        </p>
                        {data.description?.map((description) => (
                            <p key={description} className="text-base">
                                {description}
                            </p>
                        ))}
                    </>
                ))}
            </div>
        </section>
    );
};

export const FeatureList: React.FC<FeatureListCardProps> = ({ List, title, Icon, className, description }) => {
    return (
        <div className={`border-solid border-l-2 py-2 ${className ? className : "bg-gray-300 border-black"}`}>
            {title ? <p className="font-semibold text-gray-900 px-6 mb-3 text-lg">{title}</p> : null}
            {description ? <p className="px-6 mb-3">{description}</p> : null}
            {List.map((data) => {
                return (
                    <div key={`${data.listItem}`}>
                        <div className="flex gap-3 px-5 py-1 items-center">
                            <span className="self-start pt-1">{Icon ? Icon : <IoIosCheckmarkCircle />}</span>
                            <p className="text-gray-900 text-base">{data.listItem}</p>
                        </div>

                        <ul>
                            {data.nestedListItem?.map((data) => {
                                return (
                                    <li className="mx-12" key={data}>
                                        {data}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
};

export const ClientFeedback: React.FC<ClientFeedbackProps> = ({ authorName, descriptions, designationWithServiceName, className }) => {
    return (
        <div className={`pt-3 pb-2  border-solid border-l-2 ${className ? className : "border-black bg-gray-100"}`}>
            {isJSXElement(descriptions) ? descriptions : <p className="px-4 italic font-semibold mb-10 text-gray-900">{descriptions}</p>}

            <div className="flex flex-col gap-y-1">
                <p className="font-bold px-4">{authorName}</p>
                <p className="text-gray-600 px-4"> {designationWithServiceName}</p>
            </div>
        </div>
    );
};

export type NavLink = {
    id: string;
    label: string;
};

type NavbarProps = {
    links: NavLink[];
};

export const Navbar: React.FC<NavbarProps> = ({ links }) => {
    const activeNavItem = links.length > 0 ? links[0].id : null;

    const [activeLink, setActiveLink] = useState<string | null>(activeNavItem);

    const handleClick = (id: string) => {
        setActiveLink(id);
    };
    return (
        <nav className="navigation">
            <div className="border-solid border-l-1 border-gray-500 flex flex-col gap-5" style={{ borderLeft: "1px solid #E5E3D4" }}>
                {links.map((link) => (
                    <a
                        href={`#${link.id}`}
                        key={link.id}
                        className={`${activeLink === link.id ? "border-l-2 border-solid border-blue-400 px-4" : "px-5 "} hover:no-underline`}
                        onClick={() => handleClick(link.id)}
                    >
                        {link.label}
                    </a>
                ))}
            </div>
        </nav>
    );
};
